import React from 'react';
import { NavigationContext } from '@react-navigation/native';
import {
  Box, HStack, Text, VStack,
} from '@gluestack-ui/themed';

import { ModuleUpgradeOfferBanner } from '@/student/module/components/ModuleUpgradeOfferBanner';
import { LabNavLink } from '@/visual-lab/LabNavLink';
import {
  UPGRADE_OFFER_LAB_CASES,
  UPGRADE_OFFER_LAB_FIXTURES,
  parseUpgradeOfferLabCase,
} from './upgradeOfferLabFixture';

export const UPGRADE_OFFER_LAB_PATH = '/visual-lab/upgrade-offer';

/** The banner only opens its dialog on a focused screen; the lab has no navigator. */
const FOCUSED_SCREEN = {
  isFocused: () => true,
  addListener: () => () => undefined,
} as unknown as React.ContextType<typeof NavigationContext>;

type UpgradeOfferLabScreenProps = {
  search: string;
  onNavigate: (path: string) => void;
};

/**
 * UpgradeOfferLabScreen
 * ----
 * The real student upgrade ribbon and dialog with fixture offers, to judge
 * the dialog's fit at a given window height. The update button calls the
 * local API with a fake module id and fails with a toast.
 */
export function UpgradeOfferLabScreen({ search, onNavigate }: UpgradeOfferLabScreenProps) {
  const labCase = parseUpgradeOfferLabCase(search);

  return (
    <NavigationContext.Provider value={FOCUSED_SCREEN}>
      <VStack space="md" px="$5" py="$4" maxWidth={960} width="100%" alignSelf="center">
        <HStack space="sm" alignItems="center">
          <Text size="xs" color="$textSubtle">Case</Text>
          {UPGRADE_OFFER_LAB_CASES.map((id) => (
            <LabNavLink
              key={id}
              accessibilityRole="link"
              href={`${UPGRADE_OFFER_LAB_PATH}?case=${id}`}
              onPress={() => onNavigate(`${UPGRADE_OFFER_LAB_PATH}?case=${id}`)}
            >
              <Text
                size="xs"
                color={id === labCase ? '$textHeading' : '$textSubtle'}
                fontWeight={id === labCase ? 'semibold' : 'normal'}
              >
                {id}
              </Text>
            </LabNavLink>
          ))}
        </HStack>
        <Box key={labCase}>
          <ModuleUpgradeOfferBanner
            courseId="lab"
            moduleId="lab"
            upgradeOffer={UPGRADE_OFFER_LAB_FIXTURES[labCase]}
          />
        </Box>
      </VStack>
    </NavigationContext.Provider>
  );
}
